import React, { useEffect, useMemo, useState } from 'react';
import { useTareas } from '../../context/TareasContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import { ArticuloCompra } from '../../types';
import { botonSecundario, chip, claseInput } from '../../components/tareas/ui';
import { Check, Plus, ShoppingCart, Trash2, X } from 'lucide-react';
import { vibrar } from '../../utils/hapticos';
import { leerHistorial, normalizar, recordar, sugerenciasCompra } from '../../utils/compra';

const CLAVE_CASA = 'rzcentral.compra.casa';
const leer = () => { try { return localStorage.getItem(CLAVE_CASA); } catch { return null; } };
const guardar = (v: string) => { try { localStorage.setItem(CLAVE_CASA, v); } catch { /* sin almacenamiento */ } };

/**
 * La lista de la compra de cada casa: cualquiera apunta, quien va a comprar
 * tacha y se ve al momento en todos los móviles. Lo tachado queda abajo
 * («En el carro») hasta vaciarlo, y lo comprado otras veces se puede volver a
 * apuntar de un toque. Gratis para todos.
 */
export const Compra: React.FC = () => {
  const { casas, compra, cargando, anadirCompra, marcarComprado, quitarCompra } = useTareas();
  const confirmar = useConfirmar();
  // Por defecto, la primera casa compartida: la compra suele ser de todos
  const porDefecto = (casas.find(c => !c.personal) ?? casas[0])?.id;
  const [casaId, setCasaId] = useState<string | undefined>(() => leer() ?? undefined);
  const casa = casas.find(c => c.id === casaId) ?? casas.find(c => c.id === porDefecto);
  useEffect(() => { if (!cargando && casaId && !casas.some(c => c.id === casaId)) setCasaId(undefined); }, [cargando, casas, casaId]);

  const [texto, setTexto] = useState('');
  const [error, setError] = useState('');

  const deCasa = compra.filter(a => a.casaId === casa?.id);
  const pendientes = deCasa.filter(a => !a.comprado).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  const enCarro = deCasa.filter(a => a.comprado)
    .sort((a, b) => (b.compradoEn?.getTime() ?? 0) - (a.compradoEn?.getTime() ?? 0));
  const [historial, setHistorial] = useState(leerHistorial);
  const nombresPendientes = pendientes.map(a => a.nombre).join('\n');
  const sugerencias = useMemo(
    () => sugerenciasCompra(historial, nombresPendientes ? nombresPendientes.split('\n') : [], texto, texto ? 6 : 10),
    [historial, texto, nombresPendientes]);

  const elegirCasa = (id: string) => { setCasaId(id); guardar(id); };

  const anadir = async (nombre: string) => {
    const limpio = nombre.trim().replace(/\s+/g, ' ').slice(0, 120);
    if (!limpio || !casa) return;
    setTexto('');
    setError('');
    setHistorial(recordar(limpio));
    // Si ya estaba en el carro, vuelve a la lista en vez de duplicarse
    const igual = deCasa.find(a => normalizar(a.nombre) === normalizar(limpio));
    try {
      if (igual?.comprado) await marcarComprado(igual, false);
      else if (!igual) await anadirCompra(casa.id, limpio);
      vibrar.suave();
    } catch (e) {
      console.error(e);
      setError('No se pudo apuntar. Inténtalo de nuevo.');
    }
  };

  const tachar = (a: ArticuloCompra) => {
    marcarComprado(a, !a.comprado).then(() => vibrar.suave()).catch(e => console.error(e));
  };

  const vaciar = async () => {
    if (!(await confirmar({
      titulo: '¿Vaciar el carro?',
      mensaje: `Se quitan de la lista las ${enCarro.length} cosas tachadas. Seguirán saliendo como sugerencia al escribir.`,
      accion: 'Vaciar',
    }))) return;
    quitarCompra(enCarro).catch(e => console.error(e));
  };

  if (cargando && casas.length === 0) {
    return <div className="max-w-2xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  const fila = (a: ArticuloCompra) => (
    <li key={a.id} className="flex items-center gap-3 px-3 py-2.5">
      <button onClick={() => tachar(a)} aria-label={a.comprado ? `Devolver «${a.nombre}» a la lista` : `Tachar «${a.nombre}»`}
        className={`w-7 h-7 shrink-0 rounded-full border-2 flex items-center justify-center transition ${
          a.comprado ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300 dark:border-gray-600 hover:border-lumbre-500'}`}>
        {a.comprado && <Check className="w-4 h-4" />}
      </button>
      <span className={`flex-1 min-w-0 break-words ${a.comprado ? 'line-through text-gray-400' : 'text-gray-900 dark:text-white'}`}>
        {a.nombre}
      </span>
      <button onClick={() => quitarCompra([a]).catch(e => console.error(e))} aria-label={`Quitar «${a.nombre}»`}
        className="p-1.5 rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
        <X className="w-4 h-4" />
      </button>
    </li>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Compra</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          Lo que falta en casa. Lo apunta cualquiera y lo ve toda la casa al momento.
        </p>
      </header>

      {casas.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {casas.map(c => (
            <button key={c.id} onClick={() => elegirCasa(c.id)} className={chip(c.id === casa?.id)}>
              {c.icono} {c.nombre}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={e => { e.preventDefault(); anadir(texto); }} className="flex gap-2">
        <input type="text" value={texto} onChange={e => setTexto(e.target.value)} maxLength={120}
          placeholder="Leche, pan, detergente…" aria-label="Qué falta" className={claseInput} enterKeyHint="done" />
        <button type="submit" disabled={!texto.trim()} aria-label="Apuntar"
          className="shrink-0 px-3 rounded-lg bg-lumbre-600 hover:bg-lumbre-700 text-white disabled:opacity-50 transition">
          <Plus className="w-5 h-5" />
        </button>
      </form>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

      {sugerencias.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" aria-label="Otras veces">
          {sugerencias.map(s => (
            <button key={s} onClick={() => anadir(s)} className={`${chip(false)} inline-flex items-center gap-1`}>
              <Plus className="w-3 h-3" /> {s}
            </button>
          ))}
        </div>
      )}

      {pendientes.length === 0 && enCarro.length === 0 ? (
        <div className="text-center py-14 mt-4 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <ShoppingCart className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-700 dark:text-gray-300 font-medium">No falta nada.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Apunta arriba lo que se acabe.</p>
        </div>
      ) : (
        <>
          {pendientes.length > 0 && (
            <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-700 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              {pendientes.map(fila)}
            </ul>
          )}
          {enCarro.length > 0 && (
            <section className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-gray-600 dark:text-gray-400">En el carro ({enCarro.length})</h2>
                <button onClick={vaciar} className={`${botonSecundario} !px-3 !py-1.5 text-xs`}>
                  <Trash2 className="w-3.5 h-3.5" /> Vaciar
                </button>
              </div>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-xl bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                {enCarro.map(fila)}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
};
